import { useLazyQuery } from '@apollo/client/react';
import {
  Combobox,
  ComboboxEmpty,
  ComboboxItem,
  RadioGroup,
  RadioTile,
  Select,
  SelectItem,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';
import { useState } from 'react';

import { addressSearch } from '@features/Address/AddressPicker/query';
import { formatAddress } from '@features/Address/formatAddress';
import { Address, BasketSummary, ProductType } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { BooleanSelection } from '@src/types/index';
import { formatCurrency } from '@utils/format';
import { validatePostcode } from '@utils/validators';

import { myBaskets, referrerDetails } from './query';
import { type Option, PartnerFormValues } from './types';

const getAddressFields = (address: Address) => {
  const {
    postcode,
    subBuilding,
    buildingName,
    buildingNumber,
    thoroughfare,
    postTown,
    uprn,
  } = address;
  return {
    postcode,
    subBuilding,
    buildingName,
    buildingNumber,
    thoroughfare,
    postTown,
    uprn,
  };
};
export const reduceServicesLabel = (products: ProductType[]) => {
  const serviceMap = {
    [ProductType.Broadband]: 'BB',
    [ProductType.Landline]: 'BB',
    [ProductType.CashbackCard]: 'CBC',
    [ProductType.Energy]: 'En',
    [ProductType.Mobile]: 'Mob',
    [ProductType.Insurance]: 'Ins',
  };
  const mappedList = products
    .filter((product) => product in serviceMap)
    .map((product) => serviceMap[product as keyof typeof serviceMap]);
  return [...new Set(mappedList)].join(', ');
};
const formatBasketOption = (basket: BasketSummary): Option => {
  const label = `${formatCurrency(basket.monthlyCost)} (${reduceServicesLabel(basket.selectedProductTypes)})`;
  return { value: basket.id, label };
};
const RestoreQuotes = ({}) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
    setFieldValue,
    setFieldError,
  }: FormikProps<PartnerFormValues> = useFormikContext();
  const getFieldProps = useFieldProps<PartnerFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });
  const [addressResults, setAddressResults] = useState<Option[]>([]);
  const [searchInput, setSearchInput] = useState<string>('');
  const [quotesResults, setQuotesResults] = useState<Option[]>([]);
  const [searchAddress, { loading: searchAddressLoading, data: addressData }] =
    useLazyQuery(addressSearch);
  const [getMyBaskets, { loading: getBasketsLoading }] =
    useLazyQuery(myBaskets);

  const [getBasketAttribution] = useLazyQuery(referrerDetails);

  const formatResultsToOptions = (addresses: Address[]): Option[] => {
    return addresses.map((address) => ({
      label: formatAddress(address),
      value: address.id,
    }));
  };

  const handleSearchInputChange = async (val: string) => {
    setSearchInput(val);

    if (validatePostcode(val)) {
      try {
        const { data } = await searchAddress({
          variables: { postCode: val },
        });

        if (!data || data.addressSearch.addresses.length === 0) {
          return;
        }
        setAddressResults(formatResultsToOptions(data.addressSearch.addresses));
      } catch {
        setFieldError('restoreQuoteAddressSearch', 'Failed to find addresses');
      }
    } else {
      setAddressResults([]);
      setQuotesResults([]);
    }
  };

  const handleSelectAddress = async (option: Option | null) => {
    if (!option) return;
    setFieldValue('restoreQuoteAddress', option);
    const selectedAddress = addressData?.addressSearch?.addresses?.find(
      (address) => address.id === option.value
    ) as Address;
    if (!selectedAddress) return;

    const supplyAddress = getAddressFields(selectedAddress);
    try {
      const { data } = await getMyBaskets({
        variables: {
          filters: {
            supplyAddress,
          },
        },
      });
      if (!data) return;
      setQuotesResults(data.myBaskets.baskets.map(formatBasketOption));
    } catch {
      setFieldError('restoreQuoteSelection', 'Failed to find baskets');
    }
  };

  const handleSelectRestore = async (basketId: string) => {
    if (!basketId) return;

    const { data, error } = await getBasketAttribution({
      variables: { basketId },
    });

    if (
      !error &&
      data?.attributionDetails?.referrer?.partner?.positionId !==
        data?.attributionDetails?.assistant?.partner?.positionId
    ) {
      setFieldValue('restoredQuoteAlreadyAttributed', true);
    } else {
      setFieldValue('restoredQuoteAlreadyAttributed', false);
    }

    setFieldValue('restoreQuoteSelection', basketId);
  };

  return (
    <>
      <RadioGroup
        {...getFieldProps('restoreQuote')}
        label="Would you like to open an existing quote?"
        direction="row"
        className="partnerAppointment__radioField"
        onValueChange={(val: string) => setFieldValue('restoreQuote', val)}
      >
        <RadioTile
          label="Yes"
          value={BooleanSelection.Yes}
          data-testid="partner-setup-restore-yes"
        />
        <RadioTile
          label="No"
          value={BooleanSelection.No}
          data-testid="partner-setup-restore-no"
        />
      </RadioGroup>

      {values.restoreQuote === BooleanSelection.Yes && (
        <>
          <Combobox
            className="partnerAppointment__restoreQuoteField"
            label="Enter the postcode of your quote and select the address"
            triggerOnlyOnType
            loading={searchAddressLoading || getBasketsLoading}
            inputValue={searchInput}
            onInputValueChange={handleSearchInputChange}
            itemToStringLabel={(item: Option) => item.label}
            onValueChange={handleSelectAddress}
            required
          >
            {addressResults.length > 0 ? (
              addressResults.map((option) => (
                <ComboboxItem key={option.value} value={option}>
                  {option.label}
                </ComboboxItem>
              ))
            ) : (
              <ComboboxEmpty>No addresses found</ComboboxEmpty>
            )}
          </Combobox>

          {values.restoreQuoteAddress && (
            <Select
              className="partnerAppointment__restoreQuoteField"
              id="restoreQuoteSelection"
              name="restoreQuoteSelection"
              helperText=" Your most recent quote for this address will appear at the top"
              required
              label="Select your quote"
              placeholder="Please select"
              value={values.restoreQuoteSelection}
              onValueChange={handleSelectRestore}
              validationStatus={
                errors.restoreQuoteSelection && submitCount > 0
                  ? 'invalid'
                  : 'valid'
              }
              validationText={
                submitCount > 0 ? errors.restoreQuoteSelection : undefined
              }
            >
              {quotesResults.length > 0 ? (
                quotesResults.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="empty" disabled>
                  No quotes found
                </SelectItem>
              )}
            </Select>
          )}
        </>
      )}
    </>
  );
};
export { RestoreQuotes };
